const OAUTH_PATH_PREFIX = "/oauth/";

export const toRelativePath = (url: string): string => {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
};

export const isSafeReturnTo = (url: string): boolean => {
  try {
    const returnUrl = new URL(url);
    if (!returnUrl.pathname.startsWith(OAUTH_PATH_PREFIX)) {
      return false;
    }
    // Standard single-host deployments serve the SPA, the API and /oauth/authorize from the same
    // origin, so a same-origin return_to is always safe. This must not depend on the baked
    // api_endpoint: in proxied envs (staging/production) it is a build-time constant pointing at an
    // unrelated host while API calls go same-origin, so it cannot be trusted to match the serving host.
    if (returnUrl.origin === window.location.origin) {
      return true;
    }
    // Cross-origin case (e.g. previews, where the UI host differs from the API/authorize host):
    // trust only hosts sharing the configured api_endpoint's base domain.
    const apiEndpoint = window.PF.config.api_endpoint;
    if (!apiEndpoint) {
      return false;
    }
    const apiUrl = new URL(apiEndpoint);
    if (returnUrl.protocol !== apiUrl.protocol) {
      return false;
    }
    if (returnUrl.hostname === apiUrl.hostname) {
      return true;
    }

    const baseDomain = apiUrl.hostname.split(".").slice(1).join(".");
    return baseDomain.length > 0 && returnUrl.hostname.endsWith(`.${baseDomain}`);
  } catch {
    return false;
  }
};

export const submitOauthSessionBridge = (token: string, returnTo: string): void => {
  const target = new URL(returnTo);
  const form = document.createElement("form");
  form.method = "POST";
  form.action = `${target.origin}/oauth/mcp_session_bridge`;

  (
    [
      ["token", token],
      ["return_to", toRelativePath(returnTo)]
    ] as const
  ).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
};

const OAUTH_RETURN_TO_KEY = "oauth_return_to";
const OAUTH_RETURN_TO_TTL_SECONDS = 600;

export const stashOauthReturnTo = (returnTo: string | null | undefined): void => {
  if (returnTo && isSafeReturnTo(returnTo)) {
    window.storage.setItem(OAUTH_RETURN_TO_KEY, returnTo, { expiresIn: OAUTH_RETURN_TO_TTL_SECONDS });
  }
};

export const takeStashedOauthReturnTo = (): string | null => {
  const stashed = window.storage.getItem(OAUTH_RETURN_TO_KEY);
  if (stashed) {
    window.storage.removeItem(OAUTH_RETURN_TO_KEY);
  }
  return stashed && isSafeReturnTo(stashed) ? stashed : null;
};
